import { useCallback, useEffect, useState } from 'react';
import { Event } from '@entities/event';
import { gql, useQuery } from 'urql';

const EventsQuery = gql`
  query GetEvents($page: Int!, $pageSize: Int!, $searchName: String) {
    getEvents(page: $page, pageSize: $pageSize, searchName: $searchName) {
      count
      total
      page
      pageCount
      events {
        id
        name
        description
        confirmed
        active
        image
        dateStart
        dateEnd
        groups {
          name
        }
        ticketsMetadata {
          activeCount
          checkedCount
          total
        }
        metadata {
          countAcceptedInvites
        }
      }
    }
  }
`;

export const EVENTS_PAGE_SIZE = 12;

export const useFetchEvents = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchValue, setSearchValue] = useState<string>('');
  const [allEvents, setAllEvents] = useState<Event[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const [{ data, fetching, error }, executeQuery] = useQuery({
    query: EventsQuery,
    variables: { page: currentPage, pageSize: EVENTS_PAGE_SIZE, searchName: searchValue },
    requestPolicy: 'network-only',
  });

  useEffect(() => {
    if (fetching) return;

    if (data?.getEvents?.events) {
      const newEvents: Event[] = data.getEvents.events;

      if (currentPage === 1) {
        setAllEvents(newEvents);
      } else {
        setAllEvents((prev) => [...prev, ...newEvents]);
      }

      const total = data.getEvents.total || 0;
      const loaded = allEvents.length + newEvents.length;
      setHasMore(loaded < total);
    }
  }, [data, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
    setAllEvents([]);
  }, [searchValue]);

  const loadMore = useCallback(() => {
    if (!fetching && hasMore) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [fetching, hasMore]);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
  };

  const refetch = useCallback(() => {
    setCurrentPage(1);
    setAllEvents([]);
    executeQuery({ requestPolicy: 'network-only' });
  }, [executeQuery]);

  return {
    events: allEvents,
    fetching,
    error,
    currentPage,
    executeQuery,
    hasMore,
    loadMore,
    handleSearchChange,
    refetch,
    total: data?.getEvents?.total || 0,
  };
};
